import { useInfiniteQuery } from '@tanstack/react-query';
import useDebounce from './useDebounce';
import { Option } from '../interfaces/Option';
import { LookupResponse } from '../interfaces/response/LookupResponse';
import { SHORT_CASHING } from '../constants/constants';

export const useInfiniteAutocomplete = (
  queryKey: string,
  fetchFn: (query: string, page: number, signal?: AbortSignal, filter?: string[]) => Promise<LookupResponse<Option>>,
  searchQuery: string,
  enabled: boolean
) => {
  const debouncedSearchQuery = useDebounce(searchQuery, 400);
  const isDebouncing = debouncedSearchQuery !== searchQuery;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [queryKey, debouncedSearchQuery],
    queryFn: ({ pageParam, signal }) =>
      fetchFn(debouncedSearchQuery, pageParam, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled,
    staleTime: SHORT_CASHING,
    gcTime: SHORT_CASHING,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const options = isDebouncing
    ? []
    : data?.pages.flatMap((page) => page.items) ?? [];

  return {
    options,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isDebouncing,
  };
};
